import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import logoSrc from "@/assets/logo.png";

export const Route = createFileRoute("/login")({
  head: () => ({ meta: [{ title: "Login — Code Cubs Academy" }] }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate({ to: "/dashboard" });
  }, [loading, user, navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("1. Starting submit");
    setBusy(true);
    try {
      console.log("2. Awaiting supabase auth");
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      console.log("3. Supabase returned", error);
      if (error) {
        toast.error(error.message);
        console.log("4. Showing error toast");
      } else {
        toast.success("مرحباً بعودتك 👋");
        console.log("4. Showing success toast");
      }
    } catch (err: any) {
      console.error("Login error:", err);
      toast.error(err.message || "حدث خطأ غير متوقع أثناء تسجيل الدخول");
    } finally {
      console.log("5. Finally block, setting busy=false");
      setBusy(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-16 max-w-md">
      <Card className="rounded-3xl border-0 shadow-soft">
        <CardContent className="p-8">
          <div className="flex justify-center mb-6">
            <img src={logoSrc} alt="Code Cubs Academy" className="h-32 w-auto object-contain" />
          </div>
          <h1 className="text-3xl font-extrabold text-center mb-2">Welcome back 👋</h1>
          <p className="text-center text-muted-foreground mb-6">سجل دخولك للوصول لـ dashboard</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email / Username</Label>
              <Input
                id="email"
                type="text"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-primary hover:underline font-medium"
                >
                  نسيت كلمة المرور؟
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full rounded-full h-12 bg-gradient-primary text-primary-foreground border-0"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>

          <p className="text-center text-sm mt-6 text-muted-foreground">
            معندكش حساب؟{" "}
            <Link to="/signup" className="text-primary font-semibold">
              أنشئ حساب
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
